import * as React from 'react';

import { cn } from '@/lib/utils';
import { buttonVariants } from './button';
import { Icon } from './icon';

const Pagination = ({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = React.ComponentPropsWithoutRef<'a'> & {
  isActive?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'icon';
};

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  children,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({ variant: isActive ? 'secondary' : 'ghost', size }),
      'normal-case',
      className
    )}
    {...props}
  >
    {children}
  </a>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="md"
    className={cn('gap-2 px-3', className)}
    {...props}
  >
    <Icon name="chevron_left" className="icon-sm" />
    <span className="text-xs font-semibold uppercase tracking-wide">Prev</span>
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="md"
    className={cn('gap-2 px-3', className)}
    {...props}
  >
    <span className="text-xs font-semibold uppercase tracking-wide">Next</span>
    <Icon name="chevron_right" className="icon-sm" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    aria-hidden
    className={cn('flex h-10 w-10 items-center justify-center', className)}
    {...props}
  >
    <Icon name="more_horiz" className="icon-sm" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
